import {
    getAvailableEvents,
    getObjectSchema,
    toWireKey,
} from '../../../Hasp/properties/v2/wireFormat.js'
import {
    getBindableProperties,
    hasRuntimeWritableMetadata,
    resolveRuntimeWireName,
} from '../../../Hasp/properties/v2/runtime.js'
import { availableStrategies } from '../../../Hasp/properties/v2/bindings.js'

const FALLBACK_LABELS = {
    val: 'Value',
    start: 'Range start',
    text: 'Text',
    color: 'Color',
    r: 'Red',
    g: 'Green',
    b: 'Blue',
    h: 'Hue',
    s: 'Saturation',
}

function asArray(value) {
    return Array.isArray(value) ? value : []
}

function normalizeVariable(variable, fallbackName = '') {
    const source = typeof variable === 'string' ? { name: variable } : (variable ?? {})
    const name = String(source.name ?? fallbackName)
    if (!name) return null
    const result = {
        name,
        type: source.type ?? 'string',
        label: source.label ?? FALLBACK_LABELS[name] ?? name,
    }
    if (source.description != null && source.description !== '') result.description = source.description
    if (source.optionsFrom != null) result.optionsFrom = source.optionsFrom
    return result
}

function propertyDefinition(schema, propertyName) {
    if (schema?.properties?.[propertyName]) return schema.properties[propertyName]
    for (const part of Object.values(schema?.parts ?? {})) {
        if (part?.properties?.[propertyName]) return part.properties[propertyName]
    }
    return null
}

function payloadEntries(specification, metadata, schema) {
    if (specification && typeof specification === 'object' && !Array.isArray(specification)) {
        return Object.entries(specification)
    }
    return asArray(specification).map(name => [
        name,
        metadata?.[name] ?? propertyDefinition(schema, name) ?? {},
    ])
}

function eventPayloadVariables(eventDefinition, schema) {
    if (!eventDefinition || typeof eventDefinition !== 'object') return []
    const payload = [
        ...payloadEntries(eventDefinition.payload, eventDefinition.payloadMetadata, schema),
        ...payloadEntries(eventDefinition.rangePayload, eventDefinition.rangePayloadMetadata, schema),
    ]
    return payload.map(([name, definition]) => {
        if (typeof definition === 'string') return normalizeVariable(definition, name)
        return normalizeVariable({ ...(definition ?? {}), name }, name)
    }).filter(Boolean)
}

function propertyEntries(schema) {
    if (!schema) return []
    const entries = []
    for (const [name, definition] of Object.entries(schema.properties ?? {})) {
        entries.push({
            name,
            wireName: definition?.runtime?.wireName ?? name,
            definition: definition ?? {},
            partName: null,
            writable: definition?.runtime?.writable === true,
        })
    }
    for (const [partName, part] of Object.entries(schema.parts ?? {})) {
        for (const [name, definition] of Object.entries(part?.properties ?? {})) {
            entries.push({
                name,
                wireName: definition?.runtime?.wireName ?? toWireKey(partName, name, part),
                definition: definition ?? {},
                partName,
                writable: definition?.runtime?.writable === true,
            })
        }
    }
    return entries
}

function bindingDefinition(schema, entry) {
    const bindings = schema?.bindings?.properties ?? {}
    if (entry.partName && bindings[entry.partName]?.[entry.name]) {
        return bindings[entry.partName][entry.name]
    }
    return bindings[entry.name] ?? bindings[entry.wireName] ?? null
}

function propertyStrategies(schema, entry) {
    return availableStrategies(entry.definition?.type, bindingDefinition(schema, entry))
}

function normalizeActionDomains(schema) {
    const recommended = [
        ...asArray(schema?.automation?.supportedActions),
        ...asArray(schema?.bindings?.actions?.hass_services),
    ]
    return [...new Set(recommended.filter(value => typeof value === 'string' && value.trim()))]
}

/**
 * Single metadata boundary for Designer schema v2.
 *
 * The adapter deliberately returns plain objects. Vue components can consume
 * these objects without knowing how schema v2 stores parts, runtime metadata
 * or legacy bindings metadata.
 */
export class DesignerSchemaAdapter {
    constructor(schemaOrObject, objType = '') {
        const candidate = schemaOrObject?.schemaV2 ?? schemaOrObject
        this.schema = candidate?.properties || candidate?.events || candidate?.parts || candidate?.automation
            ? candidate
            : getObjectSchema(schemaOrObject?.obj ?? objType)
        this.objType = objType || schemaOrObject?.obj || ''
    }

    static forNode(node) {
        return new DesignerSchemaAdapter(node?.schemaV2 ?? node?.obj, node?.obj)
    }

    getProperties({ bindableOnly = false, fallbackProperties = [] } = {}) {
        const entries = propertyEntries(this.schema)
        const byWireName = new Map(entries.map(entry => [entry.wireName, entry]))
        const names = bindableOnly
            ? getBindableProperties(this.schema)
            : entries.map(entry => entry.wireName)

        // Legacy nodes can carry a bindable property that predates the schema
        // entry. Keep it visible instead of dropping saved configuration.
        for (const name of fallbackProperties ?? []) {
            if (bindableOnly && hasRuntimeWritableMetadata(this.schema) && !names.includes(name)) continue
            if (!byWireName.has(name)) {
                names.push(name)
                byWireName.set(name, { name, wireName: name, definition: {}, partName: null, writable: true })
            }
        }

        return [...new Set(names)].map(wireName => {
            const entry = byWireName.get(wireName) ?? entries.find(item => item.name === wireName)
            const definition = entry?.definition ?? {}
            return {
                name: wireName,
                authoredName: entry?.name ?? wireName,
                wireName,
                partName: entry?.partName ?? null,
                type: definition.type ?? 'string',
                label: definition.label ?? wireName,
                description: definition.description ?? '',
                default: this.defaultFor(entry?.name ?? wireName, entry?.partName),
                writable: entry?.writable === true,
                min: definition.min,
                max: definition.max,
                step: definition.step,
                values: definition.values ?? definition.options,
                strategies: entry ? propertyStrategies(this.schema, entry) : availableStrategies(definition.type),
                definition,
            }
        })
    }

    getProperty(property, { fallbackProperties = [] } = {}) {
        return this.getProperties({ fallbackProperties }).find(item =>
            item.name === property || item.authoredName === property || item.wireName === property
        ) ?? null
    }

    getBindableProperties(fallbackProperties = []) {
        return this.getProperties({ bindableOnly: true, fallbackProperties })
    }

    getEvents() {
        const events = this.schema?.events ?? {}
        return getAvailableEvents(this.schema).map(name => {
            const definition = events[name] ?? {}
            const payloadVariables = eventPayloadVariables(definition, this.schema)
            return {
                name,
                label: this.schema?.automation?.triggerLabels?.[name] ?? definition.label ?? name,
                payloadVariables,
                payloadNames: payloadVariables.map(variable => variable.name),
                payloadMetadataDefined: Boolean(
                    Object.hasOwn(definition, 'payload') ||
                    Object.hasOwn(definition, 'rangePayload') ||
                    Object.hasOwn(definition, 'payloadMetadata') ||
                    Object.hasOwn(definition, 'rangePayloadMetadata'),
                ),
                definition,
            }
        })
    }

    getEvent(eventName) {
        return this.getEvents().find(event => event.name === eventName) ?? null
    }

    getRuntimeVariables(eventName = '') {
        const declared = new Map(
            asArray(this.schema?.automation?.runtimeVariables)
                .map(variable => normalizeVariable(variable))
                .filter(Boolean)
                .map(variable => [variable.name, variable]),
        )
        const events = this.getEvents()
        const event = eventName ? events.find(item => item.name === eventName) : null
        const typedEvents = events.filter(item => item.payloadMetadataDefined)
        const payload = eventName
            ? (event?.payloadMetadataDefined ? event.payloadVariables : [])
            : typedEvents.flatMap(item => item.payloadVariables ?? [])
        const mergeLegacyDetails = variable => declared.has(variable.name)
            ? { ...variable, ...declared.get(variable.name) }
            : variable
        if (eventName && event?.payloadMetadataDefined) return dedupeVariables(payload.map(mergeLegacyDetails))
        if (!eventName && typedEvents.length > 0) return dedupeVariables(payload.map(mergeLegacyDetails))
        const variables = payload.length > 0 ? payload : [...declared.values()]
        return dedupeVariables(variables.map(variable => mergeLegacyDetails(variable)))
    }

    getRecommendedActionDomains() {
        const canonical = asArray(this.schema?.automation?.actionDomains)
        return canonical.length > 0
            ? [...new Set(canonical.filter(value => typeof value === 'string' && value.trim()))]
            : normalizeActionDomains(this.schema)
    }

    /** Return schema action hints after removing unavailable event/object references. */
    getActionHints(eventName = '', { object = null } = {}) {
        const catalogue = this.schema?.automation?.actionHints
            ?? this.schema?.automation?.dataSuggestions
            ?? {}
        // Partial legacy schema snapshots may carry only automation hints and
        // no event declarations. Preserve those hints until a complete schema
        // is available; an unknown event is not evidence that its variables
        // are invalid.
        if (eventName && !this.getEvent(eventName)) return catalogue
        const availableVariables = new Set(eventName ? this.getRuntimeVariables(eventName).map(variable => variable.name) : [])
        const availableProperties = new Set(this.getProperties().flatMap(property => [property.name, property.authoredName]))
        const objectKeys = new Set(Object.keys(object ?? {}))

        const isAvailable = suggestion => {
            const value = typeof suggestion === 'string' ? suggestion : suggestion?.value
            if (typeof value !== 'string') return false
            const variables = [...value.matchAll(/{{\s*([a-zA-Z_][\w]*)\b/g)].map(match => match[1])
            if (!eventName) return true
            if (variables.some(variable => !availableVariables.has(variable))) return false
            const objectReferences = [...value.matchAll(/(?:^|[^\w])object\.([a-zA-Z_][\w]*)/g)].map(match => match[1])
            return objectReferences.every(property => availableProperties.has(property) || objectKeys.has(property))
        }

        return Object.fromEntries(Object.entries(catalogue).map(([domain, services]) => [
            domain,
            Object.fromEntries(Object.entries(services ?? {}).map(([service, suggestions]) => [
                service,
                asArray(suggestions).filter(isAvailable),
            ])),
        ]))
    }

    getMetadata({ fallbackProperties = [] } = {}) {
        const events = this.getEvents()
        return {
            objType: this.objType,
            properties: this.getProperties({ fallbackProperties }),
            bindableProperties: this.getBindableProperties(fallbackProperties),
            events,
            triggers: events.map(event => event.name),
            runtimeVariables: this.getRuntimeVariables(),
            recommendedActionDomains: this.getRecommendedActionDomains(),
            actionHints: this.getActionHints(),
        }
    }

    selectorForProperty(fallbackProperties = []) {
        return {
            kind: 'designer-property',
            options: this.getBindableProperties(fallbackProperties).map(property => ({
                value: property.name,
                label: property.label,
                description: property.description,
            })),
            preserveUnknown: true,
        }
    }

    selectorForEvent() {
        return {
            kind: 'select',
            options: this.getEvents().map(event => ({ value: event.name, label: event.label })),
            preserveUnknown: true,
        }
    }

    selectorForDynamicValue(eventName, { type = 'text', suggestions = [] } = {}) {
        const variables = this.getRuntimeVariables(eventName)
        const options = [
            ...variables.map(variable => ({
                value: `{{ ${variable.name} }}`,
                label: variable.label,
            })),
            ...asArray(suggestions),
        ]
        return {
            kind: 'dynamic',
            literalKind: type,
            dynamicVariables: variables,
            options,
            preserveUnknown: true,
        }
    }

    defaultFor(property, partName = null) {
        if (partName) return this.schema?.defaults?.styles?.[partName]?.[property]
        return this.schema?.defaults?.properties?.[property] ?? this.schema?.defaults?.[property]
    }
}

function dedupeVariables(variables) {
    const seen = new Set()
    return variables.filter(variable => variable && !seen.has(variable.name) && seen.add(variable.name))
}

export function getDesignerSchemaMetadata(node, options) {
    return DesignerSchemaAdapter.forNode(node).getMetadata(options)
}

export function resolveDesignerWireName(schemaOrObject, property) {
    const schema = schemaOrObject?.schemaV2 ?? schemaOrObject
    return resolveRuntimeWireName(schema, property) ?? property
}

